import { supabase } from '../lib/supabase';

const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL;

/**
 * Create a Mux direct upload URL via Edge Function
 */
export async function createMuxUpload({ title = 'Untitled Video', description = '', songId = null, level = null, talentId = null } = {}) {
  const { data: { session } } = await supabase?.auth?.getSession();
  if (!session) throw new Error('Not authenticated');

  const response = await fetch(`${SUPABASE_URL}/functions/v1/create-mux-upload`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${session?.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ title, description, songId, level, talentId }),
  });

  if (!response?.ok) {
    const err = await response?.json()?.catch(() => ({ error: 'Unknown error' }));
    const message = err?.error || 'Failed to create upload';
    const details = err?.details ? ` (${err?.details})` : '';
    throw new Error(`${message}${details}`);
  }

  return response?.json();
}

/**
 * Upload video file directly to Mux upload URL with progress tracking
 */
export function uploadVideoToMux(uploadUrl, file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        const percent = Math.round((e.loaded / e.total) * 100);
        onProgress(percent);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Upload network error')));
    xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));

    xhr.open('PUT', uploadUrl);
    xhr.send(file);
  });
}

/**
 * Get playback URL for a video via Edge Function
 */
export async function getPlaybackUrl(videoId) {
  const { data: { session } } = await supabase?.auth?.getSession();
  const headers = { 'Content-Type': 'application/json' };
  if (session) headers['Authorization'] = `Bearer ${session?.access_token}`;

  const response = await fetch(
    `${SUPABASE_URL}/functions/v1/get-playback-url?videoId=${encodeURIComponent(videoId)}`,
    { headers }
  );

  if (!response?.ok) {
    const err = await response?.json()?.catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error || 'Failed to get playback URL');
  }

  return response?.json();
}

/**
 * Poll video status from Supabase until ready or errored
 */
export async function pollVideoStatus(videoId, { maxAttempts = 60, intervalMs = 5000 } = {}) {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const { data, error } = await supabase?.from('videos')?.select('id, status, mux_playback_id, thumbnail_url, duration_seconds')?.eq('id', videoId)?.single();

    if (error) throw new Error(error.message);

    if (data?.status === 'ready' || data?.status === 'errored') {
      return data;
    }

    await new Promise(resolve => setTimeout(resolve, intervalMs));
  }

  throw new Error('Video processing timed out');
}

/**
 * Update video metadata in Supabase
 */
export async function updateVideoMetadata(videoId, { title, description, published }) {
  const updates = {};
  if (title !== undefined) updates.title = title;
  if (description !== undefined) updates.description = description;
  if (published !== undefined) updates.published = published;

  const { data, error } = await supabase?.from('videos')?.update(updates)?.eq('id', videoId)?.select()?.single();

  if (error) throw new Error(error.message);
  return data;
}

/**
 * Fetch all videos (admin) or published videos (public)
 */
export async function fetchVideos({ adminMode = false } = {}) {
  let query = supabase?.from('videos')?.select('*')?.order('created_at', { ascending: false });

  if (!adminMode) {
    query = query?.eq('published', true)?.eq('status', 'ready');
  }

  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return data || [];
}

/**
 * Delete a video record from Supabase
 */
export async function deleteVideo(videoId) {
  const { error } = await supabase?.from('videos')?.delete()?.eq('id', videoId);

  if (error) throw new Error(error.message);
}

/**
 * Save a new video record using an existing Mux playback ID
 */
export async function saveVideoWithPlaybackId({ muxPlaybackId, title, description = '', published = false, songId = null, level = null, talentId = null }) {
  const { data: { session } } = await supabase?.auth?.getSession();
  if (!session) throw new Error('Not authenticated');

  const { data, error } = await supabase
    ?.from('videos')
    ?.insert({
      title: title || muxPlaybackId,
      description,
      mux_playback_id: muxPlaybackId,
      status: 'ready',
      published,
      song_id: songId,
      level,
      talent_id: talentId,
      created_by: session?.user?.id,
    })
    ?.select()
    ?.single();

  if (error) throw new Error(error.message);
  return data;
}

/**
 * Save video transcript and captions to Supabase
 */
export async function saveVideoTranscript(videoId, transcript, captions) {
  const { data, error } = await supabase
    ?.from('videos')
    ?.update({ transcript, captions, updated_at: new Date()?.toISOString() })
    ?.eq('id', videoId)
    ?.select('id, transcript, captions')
    ?.single();

  if (error) throw new Error(error.message);
  return data;
}

/**
 * Fetch video captions from Supabase
 */
export async function getVideoTranscript(videoId) {
  const { data, error } = await supabase
    ?.from('videos')
    ?.select('id, transcript, captions')
    ?.eq('id', videoId)
    ?.single();

  if (error) throw new Error(error.message);
  return data;
}
